// Pure parsing layer for the PDF request-form import (no pdfjs dependency,
// so it's unit-testable in jsdom). pdfImport.ts feeds it positioned text
// snippets - regular text-layer items plus AcroForm annotation values, since
// fillable forms keep the typed values in annotations that getTextContent
// never sees - and gets back the field bag for the add-request modal.

export interface ExtractedFields {
  organisation?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  aansluiter_name?: string;
  aansluiter_email?: string;
  aansluiter_phone?: string;
  project_name?: string;
  address?: string;
  /** Normalised to yyyy-MM-dd (the add-form inputs are type="date"). */
  start_at?: string;
  end_at?: string;
  max_current_a?: string;
  /** Station IDs as they appear in the PDF (e.g. "PS-1234" or
      "Erasmuspark-1056CJ-2"). Matched against the admin add-request
      dropdown's powerStationId on add. */
  station_ids?: string[];
  /** Extra context (chosen connection type, permit, water) that has no form
      field of its own - appended to the internal note. */
  notes?: string[];
}

/** One positioned snippet: a text-layer item or an annotation value. */
export interface TextEntry {
  x: number;
  y: number;
  text: string;
}

/** Marker emitted for a checked radio/checkbox annotation. */
export const CHECKED = '[X]';

/**
 * Rebuilds reading order from positioned snippets: group by line (PDF y
 * coordinates grow upwards, so sort descending), left-to-right within a
 * line. Annotation values land on the same visual line as their printed
 * label, which is what lets the label→value regexes below work on fillable
 * forms.
 */
export function buildTextLayout(entries: TextEntry[], lineTolerance = 9): string {
  const sorted = [...entries]
    .filter((e) => e.text.trim().length > 0)
    .sort((a, b) => b.y - a.y || a.x - b.x);
  const lines: string[] = [];
  let current: TextEntry[] = [];
  for (const e of sorted) {
    if (current.length > 0 && current[0].y - e.y > lineTolerance) {
      lines.push(flushLine(current));
      current = [];
    }
    current.push(e);
  }
  if (current.length > 0) lines.push(flushLine(current));
  return lines.join('\n');
}

function flushLine(entries: TextEntry[]): string {
  return entries
    .sort((a, b) => a.x - b.x)
    .map((e) => e.text.trim())
    .filter(Boolean)
    .join(' ');
}

/**
 * Normalises the date spellings seen on request forms to yyyy-MM-dd. Dutch
 * forms are day-first: "26/6/26", "01-06-2026", "1.6.26"; ISO passes
 * through. Returns '' when the input doesn't look like a date.
 */
export function toIsoDate(raw: string): string {
  const s = raw.trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return s;
  m = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
  if (!m) return '';
  const day = Number(m[1]);
  const month = Number(m[2]);
  let year = Number(m[3]);
  if (year < 100) year += 2000;
  if (month < 1 || month > 12 || day < 1 || day > 31) return '';
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * PDF text layers fragment words arbitrarily ("Wie sluit" can extract as
 * "W ie sluit"), so label phrases are matched with an optional space
 * allowed between every character. Regex specials in the label are escaped.
 */
function fuzzy(label: string): string {
  return label
    .split('')
    .map((ch) => (ch === ' ' ? '[ ]+' : ch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '[ ]?'))
    .join('');
}

/** Matches the Gemeente Amsterdam "openbare stroomvoorzieningen" form. */
function isMunicipalForm(text: string): boolean {
  return new RegExp(fuzzy('Aanvraagformulier gebruik openbare stroomvoorzieningen'), 'i').test(text)
    || new RegExp(fuzzy('Wie sluit de stroom aan'), 'i').test(text);
}

export function extractFields(text: string): ExtractedFields {
  return isMunicipalForm(text) ? extractMunicipalFields(text) : extractGenericFields(text);
}

/** First capture after any of the labels (fragmentation-tolerant), scoped to `scope`. */
function find(scope: string, labels: string[], maxChars = 80): string {
  for (const label of labels) {
    const re = new RegExp(fuzzy(label) + '\\s*[:.]?\\s*([^\\n\\r]{1,' + maxChars + '})', 'i');
    const m = scope.match(re);
    if (m) {
      const v = m[1].trim()
        .replace(/\s+(Naam|Adres|E[- ]?mail|Mailadres|Telefoon|Stad|Postcode|Project|Periode|Stroom|Vermogen|Capaciteit|Current|Datum)\b.*$/i, '')
        .trim();
      if (v.length > 1) return v;
    }
  }
  return '';
}

function findEmail(scope: string): string {
  const m = scope.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
  return m ? m[0] : '';
}

/**
 * Gemeente Amsterdam template. Section layout:
 *   - station list after "…stroompunt(en) u wenst te gaan gebruiken"
 *   - connection-type + permit radio groups ([X] marks the checked one)
 *   - date row (values are the only date-shaped tokens on the form)
 *   - "Wie sluit de stroom aan?"  → the technical CONNECTOR (aansluiter)
 *   - "Gegevens ten behoeve van facturatie" → the REQUESTER (billing org)
 */
function extractMunicipalFields(text: string): ExtractedFields {
  const fields: ExtractedFields = {};
  const notes: string[] = [];

  // Stations: "Erasmuspark-1056CJ-2 & Erasmuspark-1056CJ-1" - split on the
  // separators people actually type (&, comma, semicolon, " en ").
  const stationsRaw = find(text, ['wenst te gaan gebruiken'], 240);
  if (stationsRaw) {
    const ids = stationsRaw
      .split(/[,;&]|\s+en\s+/i)
      .map((s) => s.trim())
      .filter((s) => s.length > 0 && /^[A-Za-z0-9_-]+$/.test(s));
    if (ids.length > 0) fields.station_ids = ids;
  }

  // Section boundaries. The connector block sits between "Wie sluit de
  // stroom aan?" and the billing header; requester (billing) follows.
  const connectorStart = text.search(new RegExp(fuzzy('Wie sluit de stroom aan'), 'i'));
  const billingStart = text.search(new RegExp(fuzzy('Gegevens ten behoeve van facturatie'), 'i'));
  const connectorScope = connectorStart >= 0
    ? text.slice(connectorStart, billingStart > connectorStart ? billingStart : undefined)
    : '';
  const billingScope = billingStart >= 0 ? text.slice(billingStart) : '';

  if (connectorScope) {
    fields.aansluiter_name = find(connectorScope, ['Naam contactpersoon', 'Naam']);
    fields.aansluiter_phone = find(connectorScope, ['Telefoonnummer', 'Telefoon'], 30);
    fields.aansluiter_email = findEmail(connectorScope);
  }
  if (billingScope) {
    fields.organisation = find(billingScope, ['Bedrijfsnaam']);
    fields.contact_email = findEmail(billingScope);
    const street = find(billingScope, ['Adres']);
    const postcodeCity = find(billingScope, ['Postcode en plaats', 'Postcode']);
    fields.address = [street, postcodeCity].filter(Boolean).join(', ');
  }

  // Dates: the only date-shaped tokens on the form are the start/end row,
  // in that order. (The labels sit on the line above the values, so a
  // label→value regex can't see them.)
  const dates = (text.match(/\b\d{1,2}[/\-.]\d{1,2}[/\-.]\d{2,4}\b/g) ?? [])
    .map(toIsoDate)
    .filter(Boolean);
  if (dates[0]) fields.start_at = dates[0];
  if (dates[1]) fields.end_at = dates[1];

  // Checked radio/checkbox lines. The connection type carries the amperage
  // ("…32A CEE" → 32); "meer dan 63A" deliberately also yields 63 - the
  // admin sees the full wording in the note and corrects upward if needed.
  const checkedLines = text.split('\n').filter((l) => l.includes(CHECKED));
  const typeLine = checkedLines.find((l) => /fase|CEE|stroomafname/i.test(l));
  if (typeLine) {
    const label = typeLine.replace(CHECKED, '').trim();
    notes.push(`Type aansluitcontact: ${label}`);
    const amp = label.match(/(\d+)\s*A\b/);
    if (amp) fields.max_current_a = amp[1];
  }
  const permitLine = checkedLines.find((l) => /vergunning/i.test(l));
  if (permitLine) notes.push(`Vergunning: ${permitLine.replace(CHECKED, '').trim()}`);
  const waterLine = text.split('\n').find((l) => /wateraansluiting/i.test(l));
  if (waterLine?.includes(CHECKED)) notes.push('Wateraansluiting gewenst: ja');

  if (notes.length > 0) fields.notes = notes;
  return fields;
}

/** Label:value layouts (e.g. internally produced request PDFs). */
function extractGenericFields(text: string): ExtractedFields {
  const fields: ExtractedFields = {};
  fields.organisation = find(text, ['Organisation', 'Organisatie', 'Bedrijf', 'Bedrijfsnaam', 'Aanvrager', 'Naam organisatie']);
  fields.contact_name = find(text, ['Contactpersoon', 'Naam contactpersoon', 'Contact name', 'Naam', 'Aanvrager']);
  fields.contact_email = find(text, ['E-mail', 'Email', 'E mail'], 100);
  fields.contact_phone = find(text, ['Telefoon', 'Phone', 'Telefoonnummer', 'Tel'], 30);
  fields.aansluiter_name = find(text, ['Aansluiter', 'Connector', 'Technisch aansluiter', 'Installateur']);
  fields.aansluiter_email = find(text, ['E-mail aansluiter', 'Connector email', 'E-mail installateur']);
  fields.project_name = find(text, ['Project', 'Projectnaam', 'Project name']);
  fields.address = find(text, ['Locatie', 'Werklocatie', 'Address', 'Adres']);
  fields.start_at = toIsoDate(find(text, ['Startdatum', 'Start date', 'Begin datum', 'Aanvang'], 20));
  fields.end_at = toIsoDate(find(text, ['Einddatum', 'End date', 'Eind datum', 'Eindigt'], 20));
  // Max-current - accept the new "Max. stroom (A)" / "Max current" wording
  // as well as legacy capacity labels (a PDF in the wild may still use the
  // older form). The colon is mandatory here: short labels like "Stroom"
  // otherwise match inside words ("stroomvoorziening" in the title). We
  // grab the first integer so "3x80 A" / "80 Ampère" / "63 kVA" all yield
  // 80 / 80 / 63 respectively; legacy kVA values will be wrong in unit but
  // no worse than dropping the value entirely, and the admin will see the
  // number to correct.
  const currentMatch = text.match(
    /(?:Max\.?\s*stroom|Stroom|Max\.?\s*current|Ampère|Vermogen|Capacity|Capaciteit|kVA)\s*(?:\(A\))?\s*:\s*[^\n\r\d]{0,10}(\d+)/i);
  if (currentMatch) fields.max_current_a = currentMatch[1];

  // Stations are picked up as a comma/semicolon-separated list under a
  // "Stroompunten" / "Stations" / "Power points" label. Each entry is
  // then matched against the admin dropdown's data-ps-id attribute on
  // fill, so the format here is the human-readable id (e.g. "PS-1234").
  const stationsRaw = find(text, ['Stroompunten', 'Stations', 'Power points', 'Power point IDs', 'Stroompunt IDs'], 240);
  if (stationsRaw) {
    const ids = stationsRaw
      .split(/[,;&]/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0 && /^[A-Za-z0-9_-]+$/.test(s));
    if (ids.length > 0) fields.station_ids = ids;
  }

  if (fields.contact_email && !/.+@.+\..+/.test(fields.contact_email)) {
    fields.contact_email = findEmail(text);
  }
  return fields;
}
